<template>
	<Swipe
		ref="articleSwipe"
		v-model="swipeDelta"
		:swipeLeft="!!articleWrappers.left"
		:swipeRight="!!articleWrappers.right"
		@dragstart="onSwipeStart"
		@swipe="onSwipe"
		aria-live="polite"
		style="position: relative; width: 100%; height: 100%"
	>
		<ArticleView
			ref="zero"
			v-bind="articleWrappers.zero.articleProperties"
			:isCurrent="articleWrappers.zero.role === 'current'"
			:aria-hidden="articleWrappers.zero.role !== 'current'"
			:style="zeroStyles"
			:previousArticleID="articleWrappers.previous && articleWrappers.previous.articleProperties.articleID"
			@swipe-link-update="onSwipeLinkUpdate($event)"
			@navigation-update="updateNavigationTheme($event)"
			@contentUIReady="restoreScrollPosition"
		></ArticleView>
		<ArticleView
			ref="one"
			v-bind="articleWrappers.one.articleProperties"
			:isCurrent="articleWrappers.one.role === 'current'"
			:aria-hidden="articleWrappers.one.role !== 'current'"
			:previousArticleID="articleWrappers.previous && articleWrappers.previous.articleProperties.articleID"
			:style="oneStyles"
			@swipe-link-update="onSwipeLinkUpdate($event)"
			@navigation-update="updateNavigationTheme($event)"
			@contentUIReady="restoreScrollPosition"
		></ArticleView>
		<ArticleView
			ref="two"
			v-bind="articleWrappers.two.articleProperties"
			:isCurrent="articleWrappers.two.role === 'current'"
			:aria-hidden="articleWrappers.two.role !== 'current'"
			:previousArticleID="articleWrappers.previous && articleWrappers.previous.articleProperties.articleID"
			:style="twoStyles"
			@swipe-link-update="onSwipeLinkUpdate($event)"
			@navigation-update="updateNavigationTheme($event)"
			@contentUIReady="restoreScrollPosition"
		></ArticleView>
		<ArticleView
			ref="three"
			v-bind="articleWrappers.three.articleProperties"
			:isCurrent="articleWrappers.three.role === 'current'"
			:aria-hidden="articleWrappers.three.role !== 'current'"
			:previousArticleID="articleWrappers.previous && articleWrappers.previous.articleProperties.articleID"
			:style="threeStyles"
			@swipe-link-update="onSwipeLinkUpdate($event)"
			@navigation-update="updateNavigationTheme($event)"
			@contentUIReady="restoreScrollPosition"
		></ArticleView>

		<slot />
	</Swipe>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import Swipe from '@/components/ui/swipe.vue';
import { mutationTypes, actionTypes, getterTypes } from '@/types';
import { mapStateNested } from '@/tools/helpers';
import { getValueLanguageConent } from '@/tools/languageValue';
import ArticleView from '@/components/ArticleView.vue';
import { whenTransitionEnds } from '@/tools/animationHelpers';

const transitionClasses = [
	'backward-enter',
	'backward-leave',
	'forward-enter',
	'forward-leave',
	'push-enter',
	'push-leave',
];
function swipeDirectionToTransition(direction) {
	if (direction === 'left') return 'forward';
	if (direction === 'right') return 'backward';
	return 'push';
}
const roleClasses = [
	'wrapper--article--left',
	'wrapper--article--right',
	'wrapper--article--current',
	'wrapper--article--previous',
	'wrapper--article--inactive',
];
// ArticleWrapper role defines styles and classes
const roles = {
	left: 'left',
	right: 'right',
	current: 'current',
	previous: 'previous',
	empty: 'empty',
};

export default {
	components: {
		Swipe,
		ArticleView,
	},
	data() {
		return {
			articleWrappers: [],
			swipeDelta: { x: 0, y: 0, swiping: false },
			contentArticleID: null,
			articleRenderArray: [{}, {}, {}],
			styleKeysTransformMap: [0, 1, 2],
		};
	},
	// Sets header tile to tile of article, title has to change for screen reader reasons
	metaInfo() {
		return {
			title: `${
				this.currentArticle
					? getValueLanguageConent(this.currentArticle.languageContent, 'title')
					: 'loading...'
			}`, // + this.currentArticle()
		};
	},

	computed: {
		...mapState(['viewState', 'dataSourceURL', 'glossaryByKeyword', 'tour', 'swipe', 'navigation']),
		...mapStateNested('swipe', ['articleProperties']),
		...mapGetters([getterTypes.historyCur, 'currentArticle', getterTypes.getScrolledArticlePosition]),
		zeroStyles() {
			return this.generateStyles(
				this.swipeDelta.x,
				this.articleWrappers.zero.role,
				this.articleWrappers.zero.isPrevious
			);
		},
		oneStyles() {
			return this.generateStyles(
				this.swipeDelta.x,
				this.articleWrappers.one.role,
				this.articleWrappers.one.isPrevious
			);
		},
		twoStyles() {
			return this.generateStyles(
				this.swipeDelta.x,
				this.articleWrappers.two.role,
				this.articleWrappers.two.isPrevious
			);
		},
		threeStyles() {
			return this.generateStyles(
				this.swipeDelta.x,
				this.articleWrappers.three.role,
				this.articleWrappers.three.isPrevious
			);
		},
	},
	methods: {
		...mapMutations([
			mutationTypes.viewStateSetUiState,
			mutationTypes.swipeIsRouterAction,
			mutationTypes.setNavigation,
			mutationTypes.scrollSavePagePosition,
			mutationTypes.scrollSetDirection,
		]),
		...mapActions([
			actionTypes.pushUrl,
			actionTypes.swipeUpdateArticlePoperties,
			actionTypes.scrollSetWrapper,
		]),

		generateStyles(swipeX, role, isPrevious) {
			const basicStyles = {
				position: 'absolute',
				top: 0,
				bottom: 0,
				left: 0,
				width: '100%',
				willChange: 'opacity, transform',
			};
			switch (role) {
				case 'left':
					return {
						...basicStyles,
						zIndex: swipeX ? 2 : 1,
						userSelect: swipeX ? 'none' : undefined,
						pointerEvents: swipeX ? 'none' : undefined,
						transform: swipeX
							? `translate3d(calc(${this.swipeDelta.x}px - 100vw),0,0)`
							: 'translate3d(-100%,0,0)',
					};
				case 'current':
					return {
						...basicStyles,
						transform: swipeX ? `translate3d(${this.swipeDelta.x}px,0,0)` : undefined,
						userSelect: swipeX ? 'none' : undefined,
						zIndex: 2,
						pointerEvents: swipeX ? 'none' : undefined,
					};
				case 'right':
					return {
						...basicStyles,
						zIndex: swipeX ? 2 : 1,
						pointerEvents: swipeX ? 'none' : undefined,
						userSelect: swipeX ? 'none' : undefined,
						// overflowY: swipeX ? 'hidden' : 'scroll',
						transform: swipeX
							? `translate3d(calc(${this.swipeDelta.x}px + 100vw),0,0)`
							: 'translate3d(100%,0,0)',
					};
				default:
					return {
						...basicStyles,
						zIndex: isPrevious ? 1 : 0,
						pointerEvents: swipeX ? 'none' : undefined,
						userSelect: swipeX ? 'none' : undefined,
						display: isPrevious ? undefined : 'none',
					};
			}
		},
		onSwipeStart() {},

		onSwipeLinkUpdate({ leftArticleProperties, rightArticleProperties }) {
			if (leftArticleProperties || rightArticleProperties) {
				this.swipeUpdateArticlePoperties({ leftArticleProperties, rightArticleProperties });
			}
		},

		async onSwipe({ direction }) {
			if (direction === null) {
				return; // null is delivered if user had not swiped under the threashold
			}
			this.swipeIsRouterAction(true);

			const { exhibitionID, tourID, articleID } = this.articleWrappers[direction].articleProperties;
			this.pushUrl({
				url: `/${exhibitionID}/${tourID ? `tour/${tourID}/` : ''}${articleID}`,
				replace: false,
			});
		},
		updateArticleWrappers(articleProperties, setPrevious) {
			// Begin Transaction
			this.articleWrappers.current = articleProperties.current;
			this.articleWrappers.left = articleProperties.left;
			this.articleWrappers.right = articleProperties.right;
			this.articleWrappers.commitChanges(setPrevious);

			// update role Clases
			this.articleWrappers.wrappers.forEach((wrapper) => {
				roleClasses.forEach((className) => {
					wrapper.$el.classList.remove(className);
				});
				wrapper.$el && wrapper.$el.classList.add(`wrapper--article--${wrapper.role || 'inactive'}`);
				wrapper.$el && wrapper.isPrevious && wrapper.$el.classList.add('wrapper--article--previous');
			});

			if (this.articleWrappers.previous) {
				this.scrollSavePagePosition({
					...this.articleWrappers.previous.articleProperties,
					scroll: this.articleWrappers.previous.$el.scrollTop,
				});
			}
			// set new ScrollWrapper to track scroll events
			this.scrollSetWrapper(this.articleWrappers.current.$el);
		},
		updateNavigationTheme(theme) {
			this.setNavigation({ navigation: { theme } });
		},
		restoreScrollPosition(articleComponent) {
			const scroll = this.getScrolledArticlePosition(
				{
					exhibitionID: articleComponent.$props.exhibitionID,
					articleID: articleComponent.$props.articleID,
					historyKey: articleComponent.$props.historyKey,
				},
				this.$store.state
			);
			articleComponent.$el.scrollTo(0, scroll);

			requestAnimationFrame(() => {
				articleComponent.$el.scrollTo(0, scroll);
				// set Scroll direction up after page change
				this.scrollSetDirection('up');
			});
		},
	},

	watch: {
		articleProperties(newProperties) {
			this.updateArticleWrappers(newProperties, this.swipe.updatedBy == 'router');
			// check if Animation classes has be apllied
			if (this.swipe.updatedBy == 'router') {
				if (this.swipe.isRouterAction) {
					this.swipeIsRouterAction(false);
					this.$refs.articleSwipe.reset();
				} else {
					// router action needs animation classes if it is not swiped by user
					const { direction } = this.swipe;
					if (this.articleWrappers.previous)
						this.articleWrappers.previous.$el.classList.add(`${swipeDirectionToTransition(direction)}-leave`);
					if (this.articleWrappers.current)
						this.articleWrappers.current.$el.classList.add(`${swipeDirectionToTransition(direction)}-enter`);

					// Wait for repainting to check for starting animations and transistions
					requestAnimationFrame(() => {
						[this.articleWrappers.previous, this.articleWrappers.current]
							.filter((articleWrapper) => !!articleWrapper)
							.forEach((articleWrapper) => {
								whenTransitionEnds({
									el: articleWrapper.$el,
									cb: () => {
										transitionClasses.forEach((className) => {
											articleWrapper.$el.classList.remove(className);
										});
									},
								});
							});
					});
				}
			}
		},
	},
	beforeMount() {
		this.articleWrappers = {
			wrappers: [],
			deferedActions: [],
			_isEqual(a, b) {
				return a && b && a.articleID === b.articleID && a.exhibitionID === b.exhibitionID;
			},
			_getByAttribute(attribute, value) {
				return this.wrappers.find((wrapper) => wrapper[attribute] === value) || null;
			},
			_getByArticleProperties(arp) {
				return this.wrappers.find((wrapper) => this._isEqual(wrapper.articleProperties, arp)) || null;
			},
			_setNewRole(role, articleProperties) {
				if (!articleProperties) return null;
				const wrapper = this._getByArticleProperties(articleProperties);
				// history is set first and history is not alowed to be overwritten by new Article, to prevent glitches during loading
				// on corner case history and new article are the same
				if (wrapper && !wrapper.nextRole && !(wrapper.nextIsPrevious && role === 'current')) {
					wrapper.nextRole = role;
					wrapper.nextArticleProperties = articleProperties;
				} else
					this.deferedActions.push({
						nextRole: role,
						nextArticleProperties: articleProperties,
					});
			},
			_initalWrapper(ref) {
				return {
					$el: null,
					role: roles.none,
					isPrevious: false,
					ref,
					articleProperties: {},
					nextRole: null,
					nextArticleProperties: {},
					nextIsHistory: false,
				};
			},
			commitChanges(setPrevious) {
				if (setPrevious) {
					// old Current wrapper will be now markted as new previous one
					const wrapper = this._getByAttribute('role', 'current');
					if (wrapper) {
						wrapper.nextIsPrevious = true;
						wrapper.nextArticleProperties = wrapper.articleProperties;
					}
				} else {
					// preserve current previous
					const wrapper = this._getByAttribute('isPrevious', true);
					if (wrapper) {
						wrapper.nextIsPrevious = true;
						wrapper.nextArticleProperties = wrapper.articleProperties;
					}
				}
				// apply all deffered Actions
				this.deferedActions.forEach((action) => {
					if (action.done) return;
					// get still unused wrappers
					const unUsedContainer = this.wrappers.find(
						(wrapper) => Object.keys(wrapper.nextArticleProperties).length === 0
					);
					Object.assign(unUsedContainer, action);
				});
				this.deferedActions = [];

				// commit changes
				this.wrappers.forEach((wrapper) => {
					wrapper.role = wrapper.nextRole;
					// set new articleProperties only if there are new ones
					wrapper.articleProperties =
						Object.keys(wrapper.nextArticleProperties).length > 0
							? wrapper.nextArticleProperties
							: wrapper.articleProperties;
					wrapper.isPrevious = wrapper.nextIsPrevious;

					// clear next Values
					wrapper.nextRole = null;
					wrapper.nextArticleProperties = {};
					wrapper.nextIsPrevious = false;
				});
			},
			get zero() {
				return this._getByAttribute('ref', 'zero') || this._initalWrapper('one');
			},
			get one() {
				return this._getByAttribute('ref', 'one') || this._initalWrapper('one');
			},
			get two() {
				return this._getByAttribute('ref', 'two') || this._initalWrapper('two');
			},
			get three() {
				return this._getByAttribute('ref', 'three') || this._initalWrapper('three');
			},

			get left() {
				return this._getByAttribute('role', 'left');
			},
			set left(articleProperties) {
				this._setNewRole('left', articleProperties);
			},

			get right() {
				return this._getByAttribute('role', 'right');
			},
			set right(articleProperties) {
				this._setNewRole('right', articleProperties);
			},

			get current() {
				return this._getByAttribute('role', 'current');
			},
			set current(articleProperties) {
				this._setNewRole('current', articleProperties);
			},

			get previous() {
				return this._getByAttribute('isPrevious', true);
			},
		};
	},
	mounted() {
		this.articleWrappers.wrappers = ['zero', 'one', 'two', 'three'].map((name) => ({
			$el: this.$refs[name].$el,
			role: roles.none,
			isPrevious: false,
			ref: name,
			articleProperties: {},
			nextRole: null,
			nextArticleProperties: {},
			nextIsHistory: false,
		}));
		this.updateArticleWrappers(this.swipe.articleProperties);
		// add prevent scrolling for ios
		[(this.$refs.zero, this.$refs.one, this.$refs.two, this.$refs.three)].forEach((wrapperArticle) => {
			wrapperArticle.$el.addEventListener(
				'touchmove',
				(event) => {
					if (this.swipeDelta.x) {
						event.preventDefault();
					}
				},
				false
			);
		});
	},
};
</script>
